/**
 * Partner-sharing invitation card — the ceremony's second beat, client-spliced
 * beside the invitation message the same way `guideAnnouncementCard.js` splices
 * the guide card beside the completion message (see that module's docblock for
 * the shared rationale). Producer (`buildSharingInvitationCardBlock`) and
 * renderer live in one module so the two can never disagree about whether a
 * card exists.
 *
 * The topic count is static once rendered: the beat resolves it live from the
 * ledger (`needsReviewCount`) at mount time and passes it in, and the card
 * never re-counts afterwards — no polling. The card is ONE of the two ways to
 * open the review; the phase-2 button row beside it carries the same
 * `open_sharing_ceremony` action, and both are revoked together while the
 * queue is up (the questionnaire launcher's card-plus-button pattern).
 *
 * UNLIKE the guide card, this one DOES transition, live, across four states
 * (`data-state`): `pending` (clickable, chevron) → `open` while the ceremony
 * panel it launched is up (dimmed via the shared `artifact.css`
 * `[data-state="open"]` rule, click target revoked — the questionnaire card's
 * own open treatment) → then either `completed` on an approve-success (green
 * "Shared" subtitle via `[data-state="completed"]`, chevron swapped for the
 * shared checkmark, click target revoked for good) or `unfinished` when the
 * panel closes without a submit (`Not finished — begin again`; same clickable
 * treatment as `pending` — `artifact.css` has no `[data-state="pending"]` rule
 * either, so both ride the base `.artifact`/`.artifact--clickable` styling
 * with no state-keyed CSS of their own). The guide card's "never update"
 * reasoning doesn't transfer here — guide generation finishes invisibly
 * minutes later after the user has moved on, while the ceremony is
 * synchronous and the user's eyes are on this exact card the instant its
 * panel closes. `setState` is exposed via `card.__sharingInvitationCard` (the
 * same dev-hook shape the guide card uses); `ModeOrchestrator` drives all four
 * states from its `resolveSharingDecision` handler. Static replay (a reload)
 * never reaches any live state — there is no persisted completedAt, so a page
 * load always shows "pending" regardless of what happened live; the durable
 * record of a real share lives in the registry (`/partner`), not this
 * transcript card.
 *
 * @module components/sharingInvitationCard
 */

import { eventBus } from '../core/utils.js';

/**
 * Read a single child element's integer text from the block's inner content.
 * Zero is a valid, meaningful count here (the all-decided-but-unconfirmed
 * state, per plan §3.A2/§3.C1) and is returned as `0`, not folded into the
 * "absent" case — callers MUST test `=== null` for "no/invalid field",
 * never falsy-check the return value, or a real zero silently reads as
 * missing.
 * @param {string} innerContent
 * @param {string} tag
 * @returns {number|null} The parsed non-negative integer, or null when the
 *   tag is missing, non-integer, or negative.
 */
function parseIntField(innerContent, tag) {
  const match = innerContent.match(new RegExp(`<${tag}>([\\s\\S]*?)</${tag}>`));
  if (!match) return null;
  const n = parseInt(match[1].trim(), 10);
  return Number.isInteger(n) && n >= 0 ? n : null;
}

/**
 * Build the `<sharing-invitation-card>` block for a topic count, or null when
 * there is no count to carry at all (negative/non-integer/absent — the
 * invitation envelope's `actionable` gate already guards this upstream). A
 * count of `0` IS built: it is the legitimate all-decided-but-unconfirmed
 * state ("Ready to approve"), not an error — the card must always render so
 * an invitation is never left without a click target (plan §3.C1 / F4).
 *
 * @param {number|null|undefined} count - Topics still needing review; `0` is
 *   valid and distinct from "no count".
 * @returns {string|null}
 */
export function buildSharingInvitationCardBlock(count) {
  if (!Number.isInteger(count) || count < 0) return null;
  return `<sharing-invitation-card>\n<count>${count}</count>\n</sharing-invitation-card>`;
}

/**
 * Render a `<sharing-invitation-card>` block as a bare artifact row.
 *
 * Expected XML:
 *   <sharing-invitation-card>
 *   <count>6</count>
 *   </sharing-invitation-card>
 *
 * `count` of `0` renders (the "Ready to approve" state, per C1); only a
 * missing/invalid `<count>` (i.e. `parseIntField` returning `null`) yields no
 * card.
 *
 * @param {string} innerContent - Content between opening and closing tags
 * @param {Object} [options]
 * @param {boolean} [options.interactive=true] - False for static replay: no
 *   click target, matching how the other artifact cards degrade.
 * @returns {HTMLElement|null}
 */
/**
 * Fixed estimate for every approval run, regardless of topic count: the run
 * opens on an overview of every unit in the group (`queueScope` ignores the
 * needs-review filter) with a suggested level pre-filled, and the one-by-one
 * walk is optional — so an undecided count of 1 vs. 6 changes how many are
 * real decisions, not how long the run is. The estimate is the upper bound
 * (reading each topic's preview), which is what an honest "about" should
 * promise. Revisit only if a group's SIZE becomes variable (D2b's
 * section-grain units); it's fixed at six today, so a count-derived formula
 * would just recompute this same constant every time.
 * @type {string}
 */
const PENDING_TIME_ESTIMATE = 'about 2 minutes';

/**
 * Subtitle for the `pending` (and `unfinished`-reverted-to-`pending`) card:
 * the live topic count, or — at `count === 0` — the legitimate
 * all-decided-but-not-yet-confirmed state (A2/C1), which reads as an
 * approval prompt rather than a review-count.
 * @param {number} count
 * @returns {string}
 */
function sharingSubtitle(count) {
  return count === 0
    ? `Ready to approve · ${PENDING_TIME_ESTIMATE}`
    : `${count} topic${count === 1 ? '' : 's'} · ${PENDING_TIME_ESTIMATE}`;
}

export function renderSharingInvitationCard(innerContent, { interactive = true } = {}) {
  const count = parseIntField(innerContent, 'count');
  if (count === null) return null;

  const card = document.createElement('div');
  card.className = interactive
    ? 'artifact artifact--sharing-invitation artifact--clickable'
    : 'artifact artifact--sharing-invitation';

  const inner = document.createElement('div');
  inner.className = 'artifact__inner';

  const left = document.createElement('div');
  left.className = 'artifact__left';

  const iconWrapper = document.createElement('div');
  iconWrapper.className = 'artifact__icon-wrapper';
  const icon = document.createElement('div');
  icon.className = 'artifact__icon';
  icon.innerHTML = '<svg width="22" height="22" aria-hidden="true"><use href="#icon-partner"/></svg>';
  iconWrapper.appendChild(icon);
  left.appendChild(iconWrapper);

  const content = document.createElement('div');
  content.className = 'artifact__content';

  const titleRow = document.createElement('div');
  titleRow.className = 'artifact__title-row';
  const titleEl = document.createElement('span');
  titleEl.className = 'artifact__title';
  titleEl.textContent = 'Partner Sharing';
  titleRow.appendChild(titleEl);
  content.appendChild(titleRow);

  const subtitle = document.createElement('div');
  subtitle.className = 'artifact__subtitle';
  subtitle.textContent = sharingSubtitle(count);
  content.appendChild(subtitle);

  left.appendChild(content);
  inner.appendChild(left);

  let actions = null;
  if (interactive) {
    actions = document.createElement('div');
    actions.className = 'artifact__actions artifact__actions--chevron-only';
    actions.innerHTML = `
      <svg class="artifact__chevron" width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path d="M7.5 5L12.5 10L7.5 15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>`;
    inner.appendChild(actions);
  }

  card.appendChild(inner);

  if (!interactive) return card;

  // Routes through the same `open_sharing_ceremony` action the (retired)
  // button used — no `groupId` in actionData, matching the guide card's
  // click-to-open precedent, so `ActionRouter._resolveEcho` finds nothing to
  // echo (the card itself is the acknowledgment, not a chat bubble).
  const open = () => {
    const state = card.dataset.state;
    if (state !== 'pending' && state !== 'unfinished') return;
    eventBus.emit('action:button-click', {
      actionId: 'open_sharing_ceremony',
      actionData: { action: 'open_sharing_ceremony' }
    });
  };
  card.addEventListener('click', open);
  card.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    open();
  });

  /**
   * Flip the card between its four live states. The single writer of
   * `data-state`, which the click gate above reads and the shared
   * `artifact.css` state rules style ('open' → dimmed, 'completed' → green
   * subtitle; 'pending' and 'unfinished' have no state-keyed CSS of their
   * own — both ride the base `.artifact--clickable` styling). 'open' and
   * 'completed' both revoke the click target; 'pending' (the initial state)
   * and 'unfinished' (the revert after a panel close with no submit — see
   * `resolveSharingDecision` in `ModeOrchestrator`) both restore it and can
   * launch the ceremony. Takes the target explicitly the way the guide
   * card's `setState` does, so a dev inspector can preview any state without
   * new plumbing.
   * @param {'pending'|'open'|'completed'|'unfinished'} next
   */
  const setState = (next) => {
    if (card.dataset.state === next) return;
    card.dataset.state = next;

    if (next === 'pending' || next === 'unfinished') {
      subtitle.textContent = next === 'unfinished' ? 'Not finished — begin again' : sharingSubtitle(count);
      card.classList.add('artifact--clickable');
      card.setAttribute('role', 'button');
      card.setAttribute('tabindex', '0');
      if (actions) {
        actions.className = 'artifact__actions artifact__actions--chevron-only';
        actions.innerHTML = `
          <svg class="artifact__chevron" width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <path d="M7.5 5L12.5 10L7.5 15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>`;
      }
      return;
    }

    card.classList.remove('artifact--clickable');
    card.removeAttribute('role');
    card.removeAttribute('tabindex');

    if (next === 'completed') {
      subtitle.textContent = 'Shared';
      if (actions) {
        actions.className = 'artifact__actions artifact__actions--checkmark';
        actions.innerHTML = `
          <svg class="artifact__checkmark" width="17" height="17" viewBox="0 0 20 20" aria-hidden="true">
            <use href="#icon-checkmark"/>
          </svg>`;
      }
      return;
    }

    // 'open' — the ceremony panel is up: the questionnaire card's own
    // in-progress treatment (subtitle swap, no chevron; the dim comes from
    // the shared `[data-state="open"]` rule).
    subtitle.textContent = 'Review in progress...';
    if (actions) {
      actions.className = 'artifact__actions';
      actions.innerHTML = '';
    }
  };

  card.__sharingInvitationCard = { setState };
  setState('pending');

  return card;
}

/**
 * Flatten a `<sharing-invitation-card>` block to plain text, for the
 * non-rendered fallback path (plain-text serialization of a message). A
 * missing/invalid `<count>` flattens to nothing (no card was built); `0` is
 * a real, renderable count (the "ready to approve" state) and flattens to a
 * line, same as any other count.
 *
 * @param {string} match - The full matched block including tags
 * @returns {string}
 */
export function flattenSharingInvitationCard(match) {
  const count = parseIntField(match, 'count');
  if (count === null) return '';
  const summary = count === 0
    ? 'ready to approve'
    : `${count} topic${count === 1 ? '' : 's'} ready to review`;
  return `\n**Partner Sharing** — ${summary}\n`;
}
